import React, {useEffect} from 'react';
import {I18n} from 'react-redux-i18n';
import {withStyles, WithStyles} from '@material-ui/core';
import {useDispatch, useSelector} from 'react-redux';
import {STATES_ENTITY} from '../../constants';
import {Switch} from '../../components';
import {getDataExportTypes} from '../../app-common/DataExport/action';
import {DataExportModel} from '../../types/DataExport';
import {NavPills} from '../../material-dashboard-pro-react/components/index';
import {changeEditModeAndDataExportState} from './slice';
import {DataExportSettingsTab, DataExportFieldsTab, DataExportOrgsTab} from '../index';

import style from './style';

/* eslint-disable no-unused-vars */
type DataExportEditFormProps = WithStyles<typeof style> & {
  dataExport: DataExportModel;
  onFormChange?: (data: DataExportModel) => void;
  setAreSpecificFieldsValid: (isValid: boolean) => void;
  isEditMode?: boolean;
}
/* eslint-enable no-unused-vars */

function DataExportEditForm({
  dataExport,
  onFormChange = () => {},
  isEditMode = false,
  setAreSpecificFieldsValid,
  classes,
}: DataExportEditFormProps) {
  const dispatch = useDispatch();
  const accountInfo = useSelector((store: any) => store.operatorLayoutReducer.accountInfo);

  useEffect(() => {
    dispatch(changeEditModeAndDataExportState({
      isEditMode,
      state: isEditMode ? STATES_ENTITY.EDITING : STATES_ENTITY.NONE,
    }));
  }, [isEditMode, dispatch]);

  const handleInputChange = (field: keyof DataExportModel, value: any) => {
    onFormChange({
      ...dataExport,
      [field]: value,
    });
  };

  const handleTemplateChange = (data: DataExportModel) => {
    onFormChange({
      ...dataExport,
      ...data,
      collectorToken: '',
      id: dataExport.id || '',
      name: dataExport.name || '',
      description: dataExport.description || '',
    });
  };

  const handleMultipleInputsChange = (values: Partial<DataExportModel>) => {
    onFormChange({
      ...dataExport,
      ...values,
    });
  };

  useEffect(() => {
    onFormChange(dataExport);
  }, [dataExport, onFormChange]);

  useEffect(() => {
    (getDataExportTypes())(dispatch);
  }, [dispatch]);

  const tabsToRender = [
    {
      tabButton: I18n.t('dataExportPage.editForm.headers.settings'),
      tabContent: <DataExportSettingsTab
        onFieldChange={handleInputChange}
        onChangeDataExportTemplate={handleTemplateChange}
        onMultipleFieldsChange={handleMultipleInputsChange}
        setAreSpecificFieldsValid={setAreSpecificFieldsValid}
        dataExport={dataExport}
        isEditMode={isEditMode}
      />,
    },
    {
      tabButton: I18n.t('dataExportPage.editForm.headers.sendData'),
      tabContent: (
        <>
          <h3>{I18n.t('dataExportPage.editForm.sendData.headers.sendData')}</h3>

          <div className={classes.filters__switchGroup}>
            <Switch
              label={I18n.t('dataExportPage.editForm.sendData.labels.allQueries')}
              isChecked={dataExport.filters.allQueries}
              onChange={(e) => {
                const isChecked = e.target.checked;
                const newFilters = {
                  allQueries: isChecked,
                  threatsDetected: isChecked ? true : dataExport.filters.threatsDetected,
                  restrictedContent: isChecked ? true : dataExport.filters.restrictedContent,
                };
                handleInputChange('filters', newFilters);
              }}
              disabled={!isEditMode}
            />
            <div className={classes.switchAddGroup}>
              <Switch
                label={I18n.t('dataExportPage.editForm.sendData.labels.threatsDetected')}
                isChecked={dataExport.filters.threatsDetected}
                onChange={(e) => {
                  const newFilters = {
                    ...dataExport.filters,
                    threatsDetected: e.target.checked,
                  };
                  if (!newFilters.threatsDetected &&
                      !newFilters.restrictedContent &&
                      !newFilters.allQueries) {
                    newFilters.threatsDetected = true;
                  }

                  handleInputChange('filters', newFilters);
                }}
                disabled={!isEditMode || dataExport.filters.allQueries}
              />

              <Switch
                label={I18n.t('dataExportPage.editForm.sendData.labels.restrictedContent')}
                isChecked={dataExport.filters.restrictedContent}
                onChange={(e) => {
                  const newFilters = {
                    ...dataExport.filters,
                    restrictedContent: e.target.checked,
                  };
                  if (!newFilters.threatsDetected &&
                      !newFilters.restrictedContent &&
                      !newFilters.allQueries) {
                    newFilters.restrictedContent = true;
                  }

                  handleInputChange('filters', newFilters);
                }}
                disabled={!isEditMode || dataExport.filters.allQueries}
              />
            </div>
          </div>
        </>
      ),
    },
    {
      tabButton: I18n.t('dataExportPage.editForm.headers.fields'),
      tabContent: (<DataExportFieldsTab
        onFieldChange={handleInputChange}
        dataExport={dataExport}
        isEditMode={isEditMode}
      />),
    },
  ];
  /*
  {
    tabButton: I18n.t('dataExportPage.editForm.headers.fields'),
    tabContent: (
      <div>TODO</div>
    ),
  }, */
  if (accountInfo?.organizationsAllowed) {
    tabsToRender.push(
      {
        tabButton: I18n.t('dataExportPage.editForm.headers.organizations'),
        tabContent: <DataExportOrgsTab
          onFieldChange={handleInputChange}
          dataExport={dataExport}
          isEditMode={isEditMode}
        />,
      },
    );
  }

  return (
    <NavPills
      color="secondary"
      tabs={tabsToRender}
    />
  );
}

export default withStyles(style)(DataExportEditForm);
